import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges } from '@angular/core';
import { Course } from 'lms-api';
import videojs from 'video.js';
import { UserCourseDataService } from 'lms-api';
import { interval } from 'rxjs';

@Component({
  selector: 'app-video-js-player',
  templateUrl: './video-js-player.component.html',
  styleUrls: ['./video-js-player.component.scss']
})
export class VideoJsPlayerComponent implements OnInit {
  @Input() course: Course;
  @Input() splashurl: string;
  @Input() videourl: string;
  @Output() vjsPendingStatusChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private viewInitDone: boolean = false;

  private player: any;
  private ticker;

  private lastPosition: number;
  private lastPositionUpdate: number;
  private counter: number;
  private positionHistory: Array<number>;
  private maxSeekTime: number;
  private startPosition: number;
  private startPositionSet: boolean = false;
  private pendingStart: boolean = false;
  private memberTypeCanSeek: boolean = false;
  private videoElapsedLimit: number = 0;
  private timeSinceLastSeekBlock: number = 0;

  private forceVisibleSeek: boolean = false;

  constructor(private ucdService: UserCourseDataService) {
    this.ticker = interval(1000);
    this.ticker.subscribe(t => {
      if (this.player) {
        let paused = true;
        try {
          paused = this.player.paused();
        } catch (e) { }
        if (paused) return;
        this.videoElapsedLimit++;
        this.timeSinceLastSeekBlock++;
      }
    });
  }

  ngOnInit() {
    this.ucdService.User.subscribe(user => {
      this.memberTypeCanSeek = user.Roles.includes('pac_staff');
      if (this.course) {
        this.maxSeekTime = Math.max(this.maxSeekTime, user.Courses[this.course.netforum_course_id] ? user.Courses[this.course.netforum_course_id].Position : 0);
      }
    });
  }

  ngAfterViewInit() {
    this.viewInitDone = true;
    this.SetupPlayer();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.course && this.viewInitDone) {
      this.SetupPlayer();
    }
  }

  ngOnDestroy() {
    if (this.player) {
      this.player.dispose();
    }
  }

  private SetupPlayer() {
    let vjsId = this.videourl != null ? "videojs" : "videojs-" + this.course.netforum_course_id;
    if (this.player) {
      this.player.dispose();
    }
    let vjsParent = document.getElementById('vjsparent');
    if (!vjsParent) return;
    while (vjsParent.firstChild) {
      vjsParent.removeChild(vjsParent.lastChild);
    }
    let videoEl = document.createElement('video');
    videoEl.setAttribute("id", vjsId);
    videoEl.setAttribute("class", "video-js vjs-default-skin vjs-big-play-centered");
    videoEl.setAttribute("controls", "");
    videoEl.setAttribute("preload", "true");
    let sourceEl = document.createElement("source");
    if (this.videourl) {
      sourceEl.setAttribute("src", this.videourl);
    } else {
      videoEl.setAttribute("poster", this.course.splash_image_url);
      if (this.course.content_url.endsWith('m3u8')) {
        if (window.localStorage.getItem("nohls") == "1") {
          sourceEl.setAttribute("src", this.course.fallback_content_url);
        } else {
          sourceEl.setAttribute("src", this.course.content_url);
          sourceEl.setAttribute("type", "application/x-mpegURL");
        }
      } else {
        sourceEl.setAttribute("src", this.course.content_url);
      }
      videoEl.appendChild(sourceEl);
      if (this.course.fallback_content_url) {
        let fallbackEl = document.createElement("source");
        fallbackEl.setAttribute("src", this.course.fallback_content_url);
        videoEl.appendChild(fallbackEl);
      }
      if (this.course.caption_url) {
        let trackEl = document.createElement("track");
        trackEl.setAttribute("kind", "captions");
        trackEl.setAttribute("srclang", "en");
        trackEl.setAttribute("label", "English");
        trackEl.setAttribute("src", this.course.caption_url);
        videoEl.appendChild(trackEl);
      }
    }

    vjsParent.appendChild(videoEl);
    this.player = videojs(vjsId, {
      fluid: true
    });

    if (this.course) {
      this.maxSeekTime = this.startPosition = this.lastPosition = this.course.userData.Position;
      this.videoElapsedLimit = this.course.userData.Position;

      this.counter = 0;
      this.positionHistory = [];
      this.player.on('loadedmetadata', evt => {
        this.player.currentTime(this.startPosition);
        
        

        if (this.course.caption_url) {
          let ccButton = document.createElement('button');
          ccButton.setAttribute('id', 'vjs_btn_cc');
          ccButton.setAttribute('class', 'vjs-captions-control vjs-control vjs-button inactive');
          ccButton.setAttribute('type', 'button');
          ccButton.setAttribute('title', 'Toggle closed caption');
          ccButton.setAttribute('aria-disabled', 'false');
          ccButton.innerText = 'CC';
          ccButton.addEventListener("click", () => { this.toggleCC(); });
          let volumePanel = document.getElementsByClassName('vjs-volume-panel')[0];
          
          volumePanel.prepend(ccButton);
        }
      });
      this.player.on('canplaythrough', evt => {
        if (this.startPositionSet) return;
        this.player.currentTime(this.startPosition);
        this.startPositionSet = true;

        let playButton = document.getElementsByClassName('vjs-play-control')[0];

        let rewindButton = document.createElement('button');
        rewindButton.setAttribute('class', 'vjs-stepback-control vjs-control vjs-button');
        rewindButton.setAttribute('type', 'button');
        rewindButton.setAttribute('title', 'Rewind 15 seconds');
        rewindButton.setAttribute('aria-disabled', 'false');
        rewindButton.addEventListener("click", () => { this.stepBack(); });
        let icon = document.getElementById('stepback');
        icon.removeAttribute('class');
        rewindButton.appendChild(icon);
        let span = document.createElement('span');
        span.textContent = " ←15";
        rewindButton.appendChild(span);
        playButton.parentNode.insertBefore(rewindButton, playButton.nextSibling);

        //Wait for the player to get put together, then if the progress bar is double-clicked, reset position to avoid super-clicking and overflowing positionHistory
        setTimeout(() => {
          if (this.course.caption_url && window.localStorage.getItem('lms_cc') == "1") {
            this.toggleCC();
          }

          document.querySelector(".vjs-remaining-time").addEventListener('click', (evt) => {
            let mEvt = evt as MouseEvent;
            if(mEvt.detail < 2 || !mEvt.shiftKey) return;
              this.forceVisibleSeek = true;
            });

          let vjsPC = document.querySelectorAll(".vjs-progress-control");
          if (vjsPC.length > 0) {
            vjsPC[0].addEventListener('dblclick', evt => {
              this.player.currentTime(Math.min.apply(null, this.positionHistory));
            });
          }
        }, 1000);
      });
      this.player.on('timeupdate', evt => {
        this.updatePosition();
      });
      this.player.on('error', (evt) => {
        if (evt.target.player.error()?.message.includes('HLS playlist')) {
          window.localStorage.setItem("nohls", "1");
          location.reload();
        }
      });
      this.player.on('seeked', (evt, a, b) => {
        if (this.videoIsSeekable || this.timeSinceLastSeekBlock <= 5) return;
        let currentPosition = this.player.currentTime();
        if (currentPosition > this.videoElapsedLimit) {
          this.player.currentTime(this.videoElapsedLimit);
          this.timeSinceLastSeekBlock = 0;
        }
      });

      this.player.getChild("ControlBar").removeChild("seekToLive");
    }
  }

  public get videoIsSeekable(): boolean {
    return this.videourl != null || this.course.IsCEActive() || this.memberTypeCanSeek;
  }

  public toggleCC() {
    let tracks = this.player.textTracks();
    let captions = [];
    for (var i = 0; i < tracks.length; i++) {
      var track = tracks[i];
      if (track.kind === 'captions') {
        captions.push(track);
      }
    }
    if(captions.filter(c => c.mode === 'showing').length > 0) {
      window.localStorage.setItem('lms_cc', '0');
      captions.forEach(c => c.mode = 'disabled');
      document.getElementById('vjs_btn_cc')?.classList.add('inactive');
    } else {
      window.localStorage.setItem('lms_cc', '1');
      captions.forEach(c => c.mode = 'showing');
      document.getElementById('vjs_btn_cc')?.classList.remove('inactive');
    }

  }

  public stepBack() {
    this.player.currentTime(Math.max(0, this.player.currentTime() - 15));
  }

  updatePosition() {
    this.counter++;
    this.positionHistory.push(this.player.currentTime());
    this.positionHistory.splice(0, this.positionHistory.length - 10);

    this.lastPosition = this.player.currentTime();

    if (!this.pendingStart && this.course.userData.Status == 'Not Started' && this.lastPosition >= 5) {
      this.pendingStart = true;
      this.vjsPendingStatusChange.emit(this.pendingStart);
      this.ucdService.StartCourse(this.course).subscribe(course => {
        this.pendingStart = false;
        this.vjsPendingStatusChange.emit(this.pendingStart);
      });
    }

    if(this.course.IsCEActive() || this.forceVisibleSeek) {
      (document.querySelector('.vjs-progress-control') as HTMLElement).style.visibility = "visible";
    } else {
      (document.querySelector('.vjs-progress-control') as HTMLElement).style.visibility = "hidden";
    }

    if (this.lastPositionUpdate == null || this.lastPosition - this.lastPositionUpdate > 5) {
      this.lastPositionUpdate = Math.min.apply(null, this.positionHistory);
      if (this.lastPositionUpdate == 0) return;
      this.maxSeekTime = Math.max(this.maxSeekTime, this.lastPositionUpdate);
      this.ucdService.SetPosition(this.course.netforum_course_id, this.lastPositionUpdate);

      if (this.lastPositionUpdate / this.player.duration() > .99 && !this.course.IsCEActive()) {
        this.pendingStart = true;
        this.vjsPendingStatusChange.emit(this.pendingStart);
        this.ucdService.ActivateCE(this.course).subscribe(course => {
          this.pendingStart = false;
          this.vjsPendingStatusChange.emit(this.pendingStart);
        });
      } else if(!this.course.IsCEActive()) {
        console.log('CE will be available when ' + Math.floor(this.player.duration() * .01) + ' seconds remain');
      }
    }
  }
}